import React, { useEffect, useMemo } from 'react';
import type { Feature } from '@fulcrumapp/fulcrum-core';
import { Box, type SxProps, type Theme } from '@mui/material';
import ArcGisMapView from '../../shared/map/arc-gis-map-view';
import Basemap from '../../shared/map/plugins/basemap';
import LayerPickerPlugin from '../../shared/map/plugins/layer-picker-plugin';
import LayerSettingsReferenceHandler from '../../shared/map/plugins/utils/layer-settings-reference-handler';
import LayerChangeCoordinator from '../../shared/map/plugins/utils/layer-change-coordinator';
import type { MapPlugin } from '../../shared/map/arcgis-types';
import StandardLayers from '../../shared/map/plugins/standard-layers';
import GeoJsonLayers from '../../shared/map/plugins/geo-json-layers';
import PortalLayers from '../../shared/map/plugins/portal-layers';
import PublicFeatureServiceLayers from '../../shared/map/plugins/public-feature-service-layers';
import { useApp } from '../../hooks/use-app';
import { defaultExtents, type Extents } from '../../types/geo';
import PhotoMarkerPlugin from './plugins/photo-marker-plugin';
import FormRecordsPlugin from './plugins/form-records-plugin';
import type { Metadata } from './display/media-displayer';
import { hasFeatureCoordinates } from './feature-coordinates';

const PHOTO_GPS_ZOOM = 20;
const FEATURE_FALLBACK_ZOOM = 15;

const containerStyles: SxProps<Theme> = (theme) => ({
  borderLeft: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    borderLeft: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
});

interface Props {
  className: string;
  feature?: Feature;
  metadata?: Metadata;
  visible?: boolean;
}

const PhotoArcGisMapView = ({
  className, feature, metadata, visible,
}: Props): React.ReactElement => {
  const app = useApp();
  const { layerSettings } = app;
  const googleApiKey = app.globals.config.google_maps.api_key;
  const { esri_api_key: esriApiKey } = app.globals.config;

  const extents: Extents = useMemo(() => {
    const { latitude, longitude } = metadata ?? {};
    if (latitude != null && longitude != null && latitude !== 0 && longitude !== 0) {
      return { latitude, longitude, zoom: PHOTO_GPS_ZOOM };
    }
    const coords = hasFeatureCoordinates(feature);
    if (coords) {
      return { latitude: coords.latitude, longitude: coords.longitude, zoom: FEATURE_FALLBACK_ZOOM };
    }
    return defaultExtents;
  }, [metadata, feature]);

  const handler = useMemo(() => LayerSettingsReferenceHandler(layerSettings), []);
  const coordinator = useMemo(() => LayerChangeCoordinator(handler), [handler]);

  useEffect(() => {
    handler.updateInstance(layerSettings);
  }, [handler, layerSettings]);

  useEffect(() => () => {
    coordinator.destroy();
  }, [coordinator]);

  const basePlugins = useMemo<MapPlugin[]>(() => [
    Basemap(handler, googleApiKey),
    LayerPickerPlugin('media-viewer'),
    FormRecordsPlugin(layerSettings.dataLayer),
    StandardLayers(coordinator),
    GeoJsonLayers(coordinator),
    PortalLayers(coordinator, esriApiKey),
    PublicFeatureServiceLayers(coordinator),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const photoMarkerPlugin = useMemo(
    () => PhotoMarkerPlugin(feature, metadata),
    [feature, metadata],
  );

  const plugins = useMemo(
    () => [...basePlugins, photoMarkerPlugin],
    [basePlugins, photoMarkerPlugin],
  );

  return (
    <Box className={className} sx={containerStyles}>
      {visible !== false && (
        <ArcGisMapView
          extents={extents}
          plugins={plugins}
          parentView="media-viewer"
        />
      )}
    </Box>
  );
};

export default PhotoArcGisMapView;
